<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import moment from "moment";

// @ts-ignore - vue2-datepicker has no type declarations
import DatePicker from "vue2-datepicker";

import {
  ButtonSeverity,
  isNullish,
  TuButton,
  TuInputLabel,
  TuInputSelect,
  TuInputSwitch,
  TuInputText,
  TuInputTextarea,
} from "@tinyurldev/tu-ui-vue";

import { flattenDeep, union } from "lodash-es";

import ValidationMessage from "@Components/forms/ValidationMessage.vue";
import UTMBuilder from "@Modals/UTMBuilder.vue";

import V2Result from "@Modules/urls/Components/V2Result.vue";

import Tags from "@Modules/urls/Components/Tags.vue";
import { Organization } from "@Modules/teams/Data/Models/OrganizationModel";
import { Team } from "@Modules/teams/Data/Models/TeamModel";
import { UpdateUrlOptions } from "@Modules/urls/Api/UrlsApi";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory";

import useLegacyStore from "@Composables/useLegacyStore";
import { ALIAS_BEHIND_MINIMUM, ALIAS_BRANDED_BEHIND_MINIMUM } from "@Composables/useShortenerForm";
import useLimit from "@Composables/useLimit";
import useOverage from "@Composables/useOverage";
import useSubscription from "@Composables/useSubscription";

import {
  disabledExpirationDateRange,
  disabledExpirationTimeRange,
} from "@Components/tinyurl/shortener/single-form/form-helper";

import LIMITS_TYPE from "@Constants/limits-type";
import showModal from "@Helpers/functions/showModal";
import urlStringParamsToJson from "@Helpers/functions/urlStringParamsToJson";

import { TypedAlias } from "@Resources/js/laravel";
import { Domain } from "@Modules/urls/Data/Models/DomainModel";
import VisitOrganizationOrTeam from "@Modules/teams/Api/Visitors/VisitOrganizationOrTeam";

//
// Types
interface UrlFormData {
  url: string;
  domain: string;
  alias: string;
  description: string;
  tags: string[];
  expires_at: string | null;
  team: string | undefined;
}

interface UrlWithAliases {
  aliases: TypedAlias[];
}

//
// Setup
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);
const { getters } = useLegacyStore()!;
const { isSubscriptionLegacyPro } = useSubscription();

const linksLimit = useLimit(LIMITS_TYPE.LINKS_PER_MONTH);
const linksOverage = useOverage(LIMITS_TYPE.LINKS_PER_MONTH);

//
// Props
interface Props {
  organization: Organization;
  team?: Team | null;
  availableTeams?: Team[] | null;
  submit: (options: UpdateUrlOptions) => Promise<UrlAlias | boolean | null>;
  errors: Record<string, string>;
  loading: boolean;
  availableDomains?: Domain[];
  selectableDomains?: { value: string; name: string }[];
  urlAlias?: UrlAlias | null;
}

const props = withDefaults(defineProps<Props>(), {
  team: null,
  availableTeams: null,
  errors: () => ({}),
  loading: false,
  availableDomains: () => [],
  selectableDomains: () => [],
  urlAlias: null,
});

//
// Data
const isMobileView = computed(() => smallerOrEqual("xl").value);
const createdTinyurl = ref<UrlAlias | null>(null);
const mainAlias = computed(() => props.urlAlias?.mainAlias() ?? null);
const initialAlias = props.urlAlias?.mainAlias();
const isCustomExpirationVisible = ref(initialAlias?.expires_at !== null && initialAlias?.expires_at !== undefined);
const urlForm = reactive<UrlFormData>({
  url: props.urlAlias?.url ?? "",
  domain: initialAlias?.domain ?? "",
  alias: initialAlias?.alias ?? "",
  description: initialAlias?.description ?? "",
  tags: initialAlias?.tags?.map((tag) => tag.name) ?? [],
  expires_at: initialAlias?.expires_at ?? null,
  team: props.team?.slug,
});
const localErrors = ref<Record<string, string>>({});
const isSubmitting = ref(false);

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/** Returns true if the given URL already contains any utm_* query parameter */
function hasUtmParams(url: string): boolean {
  const params = new URLSearchParams(url.split("?")[1] ?? "");
  return UTM_KEYS.some((key) => params.has(key));
}

// Reflect UTM params already present in the URL (e.g. when editing an existing link)
const withUtm = ref<boolean | null>(hasUtmParams(urlForm.url) ? true : null);
const longUrlRef = ref<{ focus: () => void } | null>(null);
const aliasHelperText = ref(ALIAS_BEHIND_MINIMUM);
const tagsRef = ref<{ commitPendingTag: () => void; localError: boolean } | null>(null);

//
// Computed
const submitButtonLabel = computed(() => props.urlAlias ? "Update Link" : "Shorten Link");
const isOnMobile = computed(() => getters.isOnMobile);
const tinyurls = computed(() => getters.tinyurls);
const urls = computed(() => getters.urls);
const combinedErrors = computed(() => ({ ...props.errors, ...localErrors.value }));

const isFeatureLocked = computed(() => {
  const isLegacyProSubscriber = isSubscriptionLegacyPro(getters.getUserCurrentSubscription);
  const isWithinUnlimitedClicks = isLegacyProSubscriber && linksLimit.usedCredits.value <= getters.userOptions?.links_with_unlimited_clicks;

  if (isWithinUnlimitedClicks) {
    return false;
  }

  return !linksOverage.isOverageOn.value && linksLimit.isHardLimitReached.value;
});

const tagHistory = computed(() => {
  const getTags = (aliases: TypedAlias[]): string[] => {
    return aliases.map((alias: TypedAlias) => alias.tags).flat();
  };

  const tinyurlData: UrlWithAliases[] = tinyurls.value?.data ?? [];
  const urlData: UrlWithAliases[] = urls.value ?? [];

  const tags = [
    ...tinyurlData.map((u) => getTags(u.aliases)),
    ...urlData.map((u) => getTags(u.aliases)),
  ];

  return union(flattenDeep(tags));
});

//
// Methods
async function focusUrlInput() {
  await nextTick();

  if (longUrlRef.value && longUrlRef.value.focus) {
    longUrlRef.value.focus();
  }
}

function setExpirationDate() {
  if (!isCustomExpirationVisible.value) {
    urlForm.expires_at = null;
    return;
  }

  if (urlForm.expires_at) {
    urlForm.expires_at = moment(urlForm.expires_at).utc().format("YYYY-MM-DDTHH:mm");
  }
}

function onExpirationDateChange(val: Date | null) {
  urlForm.expires_at = val ? moment(val).utc().toISOString() : null;
}

function addUtm() {
  if (!urlForm.url) {
    localErrors.value = { url: "The URL field is required." };

    nextTick(() => {
      withUtm.value = false;
    });

    return;
  }

  showModal(UTMBuilder, {
    method: "add",
    destination: urlForm.url,
    callback: (rebuildUrl: string) => {
      urlForm.url = rebuildUrl;
    },
  });
}

function removeUtm() {
  const params: Record<string, string> = urlStringParamsToJson(urlForm.url);

  UTM_KEYS.forEach((key) => delete params[key]);

  const url = urlForm.url.split("?")[0];
  const str = new URLSearchParams(params).toString();

  urlForm.url = `${url}${str ? `?${str}` : ``}`;
}

function onDomainSelect(e: string) {
  const selectedDomain = props.availableDomains.find((domain: Domain) => domain.domain === e);

  aliasHelperText.value = selectedDomain?.isBranded
    ? ALIAS_BRANDED_BEHIND_MINIMUM
    : ALIAS_BEHIND_MINIMUM;
}

async function submitForm() {
  tagsRef.value?.commitPendingTag();

  if (tagsRef.value?.localError) {
    return;
  }

  setExpirationDate();
  localErrors.value = {};

  const options: UpdateUrlOptions = {
    orgSlug: props.organization?.slug ?? "",
    teamSlug: props.team?.slug ?? "",
    url: urlForm.url,
    domain: urlForm.domain,
    alias: urlForm.alias,
    description: urlForm.description,
    tags: urlForm.tags,
    expires_at: urlForm.expires_at,
  };

  if (props.urlAlias) {
    options.originalDomain = mainAlias.value?.domain ?? "";
    options.originalAlias = mainAlias.value?.alias ?? "";
  }

  isSubmitting.value = true;
  const result = await props.submit(options);
  isSubmitting.value = false;

  if (result) {
    if (props.urlAlias) {
      // if we have updated an existing url to a new alias then we need to update the address bar of the browser
      const aliasChanged = urlForm.domain !== mainAlias.value?.domain || urlForm.alias !== mainAlias.value?.alias;
      if (aliasChanged && mainAlias.value) {
        VisitOrganizationOrTeam({
          orgSlug: props?.organization?.slug || "",
          teamSlug: props.team?.slug,
          alias: `${urlForm.domain}/${urlForm.alias}`,
        });
      }
    } else if (typeof result !== "boolean") {
      createdTinyurl.value = result;
      result.visit();
      Bus.$emit("tinyurl-created", null);
    }
  } else {
    if (props.errors.url) {
      focusUrlInput();
    }
  }

  if (window.fbq) {
    window.fbq("track", "shortern-url-button-clicked");
  }
}

function resetCreateForm() {
  isCustomExpirationVisible.value = false;
  withUtm.value = null;
  urlForm.url = "";
  urlForm.alias = "";
  urlForm.description = "";
  urlForm.tags = [];
  urlForm.expires_at = null;
  createdTinyurl.value = null;
  focusUrlInput();
}

//
// Watchers
watch(withUtm, (isOn, wasOn) => {
  if (isOn) {
    addUtm();
  } else if (wasOn) {
    removeUtm();
  }
});

onMounted(() => {
  focusUrlInput();
  if (!props.urlAlias) {
    const firstDomain = props.availableDomains[0];
    if (firstDomain) {
      urlForm.domain = firstDomain.domain;
    }
  }
});

//
// Exposed Test Vars
defineExpose({
  urlForm,
  localErrors,
  combinedErrors,
  createdTinyurl,
  isCustomExpirationVisible,
  isSubmitting,
  withUtm,
  tagsRef,
  aliasHelperText,
  submitForm,
  resetCreateForm,
  addUtm,
  removeUtm,
  onDomainSelect,
});
</script>

<template>
  <V2Result
    v-if="!isNullish(createdTinyurl) && isNullish(urlAlias)"
    :created-tinyurl="createdTinyurl"
    test-id-prefix="dashboard"
    @on-shorten-another="resetCreateForm"
  />

  <form
    v-else
    autocomplete="off"
    class="single-form"
    :class="{ 'single-form--mobile': isMobileView }"
    @submit.prevent="submitForm"
  >
    <!-- Hidden input to prevent chrome autofill bug -->
    <input
      autocomplete="false"
      name="hidden-non-bulk"
      type="text"
      class="d-none"
    >

    <TuInputSelect
      v-model="urlForm.team"
      :label="{ id: 'team', label: 'Team', icon: 'fa-solid fa-arrows-down-to-people' }"
      placeholder="Select a Team"
      :options="availableTeams?.map((team) => ({ value: team.slug, name: team.name })) ?? []"
      :is-disabled="true"
      hidden
    />

    <!-- Long URL Field -->
    <TuInputText
      id="url"
      ref="longUrlRef"
      v-model="urlForm.url"
      class="_long_url"
      :label="{ id: 'url', label: 'Long URL', icon: 'fa-solid fa-location-arrow', isRequired: true }"
      placeholder="Paste Long URL Here"
      :is-disabled="isFeatureLocked"
      :copy="{ errorMessage: combinedErrors.form || combinedErrors.url, isHtml: false }"
    />

    <!-- Domain and Alias Field -->
    <div class="single-form__domain-and-alias">
      <button
        class="single-form__domain-and-alias__hidden-button"
        type="submit"
        tabindex="-1"
        aria-hidden="true"
      />
      <TuInputSelect
        v-model="urlForm.domain"
        :label="{ id: 'domain', label: 'Domain', icon: 'fa-solid fa-globe' }"
        :options="selectableDomains"
        :is-disabled="isFeatureLocked"
        :copy="{ errorMessage: combinedErrors.domain, isHtml: false }"
        placeholder="Select a Domain"
        class="single-form__domain-and-alias__domain-input"
        @input="onDomainSelect"
      />
      <span
        v-show="!isMobileView"
        class="single-form__domain-and-alias__separator"
      >/</span>
      <TuInputText
        id="alias"
        v-model="urlForm.alias"
        :label="{ id: 'alias', label: 'Alias (Optional)', icon: 'fa-solid fa-highlighter' }"
        placeholder="Add Alias Here"
        :is-disabled="isFeatureLocked"
        :copy="{ help: aliasHelperText, errorMessage: combinedErrors.alias, isHtml: false }"
        class="single-form__domain-and-alias__alias-input"
      />
    </div>

    <!-- Description Field -->
    <TuInputTextarea
      v-model="urlForm.description"
      :label="{ id: 'description', label: 'Write Description (Optional)', icon: 'fa-solid fa-file-lines' }"
      :copy="{ errorMessage: combinedErrors.description }"
      placeholder="Enter Description"
      class="single-form__description-input"
    />

    <!-- Tags Field -->
    <Tags
      ref="tagsRef"
      class="single-form__tags"
      :value="urlForm.tags"
      :tag-history="tagHistory"
      :disabled="isFeatureLocked"
      :tag-error="combinedErrors.tags"
      @input="(tags) => urlForm.tags = tags"
    />

    <!-- Advanced Options -->
    <TuInputLabel
      id="advance-options"
      class="single-form__advance-options"
      icon="fa-solid fa-sliders"
      label="Advanced Options"
    />

    <!-- UTM -->
    <div class="single-form__utm">
      <TuInputSwitch
        :value="withUtm ?? false"
        :is-disabled="isFeatureLocked"
        heading="Add UTM"
        @input="(val) => withUtm = val"
      />
      <i
        v-tooltip="{
          content: `Use UTM tags to get more details about your traffic sources when using analytics tools outside of TinyURL`,
          trigger: isOnMobile ? 'click' : 'focus hover',
        }"
        class="fa fa-info-circle single-form__utm__tooltip"
        tabindex="0"
      />
      <a
        v-if="withUtm"
        href="javascript:void(0)"
        class="single-form__utm__edit-utm"
        @click.prevent="addUtm()"
      >
        <i class="fa-solid fa-pencil text-sm" /> Edit
      </a>
    </div>

    <!-- Link Expiry -->
    <div
      class="single-form__expiration"
      :class="{
        'single-form__expiration--visible': isCustomExpirationVisible,
      }"
    >
      <TuInputSwitch
        :value="isCustomExpirationVisible"
        :is-disabled="isFeatureLocked"
        heading="Set Link Expiry"
        @input="(val) => isCustomExpirationVisible = val"
      />
      <small class="single-form__expiration__helper-text">(Local Time)</small>
    </div>

    <div
      v-if="isCustomExpirationVisible"
      class="single-form__expiration"
    >
      <DatePicker
        :value="urlForm.expires_at ? moment.utc(urlForm.expires_at).toDate() : null"
        class="w-100"
        type="datetime"
        format="MMM DD, YYYY hh:mm A"
        placeholder="Select date and time"
        :input-class="[
          'form-control single-form__expiration__date-picker',
          { 'is-invalid': !!combinedErrors.expires_at },
        ]"
        :disabled="isSubmitting"
        :disabled-date="disabledExpirationDateRange"
        :disabled-time="disabledExpirationTimeRange"
        @input="onExpirationDateChange"
      >
        <template #icon-calendar>
          <i class="fa-solid fa-calendar text-blue" />
        </template>
      </DatePicker>
      <ValidationMessage :message="combinedErrors.expires_at" />
    </div>

    <!-- Submit Button -->
    <TuButton
      class="_submit"
      :label="submitButtonLabel"
      :is-block="true"
      :is-disabled="isFeatureLocked || isSubmitting"
      :severity="ButtonSeverity.SECONDARY"
      :is-loading="isSubmitting"
    />
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.single-form {
  $self: &;

  &__domain-and-alias {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: spacing.$xxs;

    &__hidden-button {
      position: absolute;
      pointer-events: none;
      opacity: 0;
    }

    &__separator {
      margin: 0 spacing.$xxxs;
      margin-top: spacing.$m;

      font-weight: typography.$font-weight-semi-bold;
    }

    &__domain-input,
    &__alias-input {
      flex: 1;
      min-width: 0%;
    }
  }

  &__expiration {
    display: flex;
    align-items: center;
    margin-bottom: spacing.$xxs;

    &__helper-text {
      margin-left: spacing.$xxxs;
      font-size: typography.$xxs-font-size;
      font-weight: typography.$font-weight-regular;
    }

    &--visible {
      margin-bottom: spacing.$xxxs;
    }
  }

  &__utm {
    display: flex;
    margin-bottom: spacing.$xxxs;

    &__tooltip {
      font-size: spacing.$xxxs;
      margin-left: spacing.$xs-5;
    }

    &__edit-utm {
      cursor: pointer;
      color: color.$primary;
      font-weight: typography.$font-weight-semi-bold;
      margin-left: spacing.$xxxs;
    }
  }

  &__tags {
    margin-bottom: spacing.$xxs;
  }

  &__advance-options {
    margin-bottom: spacing.$xxs;
  }

  &--mobile {
    #{ $self }__domain-and-alias {
      display: block;
    }
  }
}
</style>

<style lang="scss">
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.single-form {
  $self: &;

  &__expiration {
    &__date-picker {
      padding-left: 30px;

      &:focus {
        color: color.$text;
        border-color: color.$secondary;
        box-shadow: 0 0 0 0.2rem color.$secondary-light;
      }

      &::placeholder {
        color: color.$text-secondary;
      }
    }
  }
}
</style>
